import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import Page from '../../components/Page/Page';
import { NetworkContext } from '../../context/NetworkContext';
import { SnackBarContext } from '../../context/SnackbarContext';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
  IconButton,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import AddIcon from '@material-ui/icons/Add';

const DURATION_OPTIONS = [
  { months: 3, label: '3 Months' },
  { months: 6, label: '6 Months' },
  { months: 12, label: '1 Year' },
  { months: 24, label: '2 Years' },
];

const emptyForm = {
  id: null,
  name: '',
  duration_months: 3,
  price: 0,
  description: '',
  status: true,
  display_order: 0,
  effective_from: '',
  grace_days_after: '',
  reminder_days_before: '',
  razorpay_enabled: true,
  razorpay_notes: '',
};

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    maxWidth: '100%',
    padding: theme.spacing(3),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  filter: {
    minWidth: 180,
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  hint: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
}));

function durationLabel(months) {
  const match = DURATION_OPTIONS.find(opt => opt.months === months);
  return match ? match.label : `${months} months`;
}

export const SubscriptionPlans = withRouter(() => {
  const classes = useStyles();
  const { sendNetworkRequest } = React.useContext(NetworkContext);
  const snackbarCtx = React.useContext(SnackBarContext);
  const [plans, setPlans] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  async function loadPlans() {
    const result = await sendNetworkRequest('/admin/listsubscriptionplans', {}, {
      status: statusFilter === 'all' ? null : statusFilter,
    }, false);
    setPlans(result?.plans || []);
  }

  function notify(result, fallback) {
    snackbarCtx.setSnack({
      ...snackbarCtx,
      open: true,
      type: result?.status === 'success' || result?.statusCode === 200 ? 'success' : (result?.status || 'error'),
      msg: result?.message || fallback,
      vertical: 'top',
    });
  }

  function openCreate() {
    setForm(emptyForm);
    setOpen(true);
  }

  function openEdit(plan) {
    setForm({
      id: plan.id,
      name: plan.name || '',
      duration_months: plan.duration_months || 3,
      price: plan.price != null ? plan.price : 0,
      description: plan.description || '',
      status: plan.status !== false,
      display_order: plan.display_order || 0,
      effective_from: plan.effective_from ? moment(plan.effective_from).format('YYYY-MM-DD') : '',
      grace_days_after: plan.grace_days_after != null ? plan.grace_days_after : '',
      reminder_days_before: plan.reminder_days_before != null ? plan.reminder_days_before : '',
      razorpay_enabled: plan.payment_config?.razorpay?.enabled !== false,
      razorpay_notes: plan.payment_config?.razorpay?.notes || '',
    });
    setOpen(true);
  }

  async function savePlan() {
    const result = await sendNetworkRequest('/admin/savesubscriptionplan', {}, {
      id: form.id,
      name: form.name,
      duration_months: form.duration_months,
      price: form.price,
      description: form.description,
      status: form.status,
      display_order: form.display_order,
      effective_from: form.effective_from || null,
      grace_days_after: form.grace_days_after === '' ? null : form.grace_days_after,
      reminder_days_before: form.reminder_days_before === '' ? null : form.reminder_days_before,
      payment_config: {
        razorpay: {
          enabled: form.razorpay_enabled,
          notes: form.razorpay_notes,
        },
      },
    }, false);
    notify(result, 'Unable to save plan');
    if (result?.status === 'success') {
      setOpen(false);
      loadPlans();
    }
  }

  async function togglePlan(plan) {
    const result = await sendNetworkRequest('/admin/togglesubscriptionplan', {}, {
      id: plan.id,
      status: !plan.status,
    }, false);
    notify(result, 'Unable to update plan status');
    if (result?.status === 'success') {
      loadPlans();
    }
  }

  useEffect(() => {
    loadPlans();
  }, [statusFilter]);

  return (
    <Page className={classes.root} title="Membership Plans">
      <div className={classes.header}>
        <Typography variant="h4">Membership Plans</Typography>
        <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Add Plan
        </Button>
      </div>
      <FormControl variant="outlined" className={classes.filter}>
        <InputLabel>Status</InputLabel>
        <Select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          label="Status"
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </Select>
      </FormControl>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Display order</TableCell>
              <TableCell>Effective from</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {plans.map(row => (
              <TableRow key={row.id}>
                <TableCell>{row.name}</TableCell>
                <TableCell>{durationLabel(row.duration_months)}</TableCell>
                <TableCell>₹{row.price}</TableCell>
                <TableCell>{row.display_order}</TableCell>
                <TableCell>
                  {row.effective_from ? moment(row.effective_from).format('DD/MM/YYYY') : '—'}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.status ? 'Active' : 'Inactive'}
                    color={row.status ? 'primary' : 'default'}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEdit(row)}>
                    <EditIcon />
                  </IconButton>
                  <Button size="small" onClick={() => togglePlan(row)}>
                    {row.status ? 'Deactivate' : 'Activate'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {plans.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">No membership plans found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{form.id ? 'Edit plan' : 'Create plan'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" className={classes.hint}>
            Leave grace and reminder empty to use Subscription Settings defaults. Razorpay is the current gateway; more gateways can be added later.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Plan name"
                variant="outlined"
                fullWidth
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Duration</InputLabel>
                <Select
                  value={form.duration_months}
                  onChange={e => setForm({ ...form, duration_months: e.target.value })}
                  label="Duration"
                >
                  {DURATION_OPTIONS.map(opt => (
                    <MenuItem key={opt.months} value={opt.months}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Price"
                type="number"
                variant="outlined"
                fullWidth
                value={form.price}
                onChange={e => setForm({ ...form, price: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Display order"
                type="number"
                variant="outlined"
                fullWidth
                value={form.display_order}
                onChange={e => setForm({ ...form, display_order: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                variant="outlined"
                fullWidth
                multiline
                rows={2}
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Effective date"
                type="date"
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.effective_from}
                onChange={e => setForm({ ...form, effective_from: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.status}
                    onChange={e => setForm({ ...form, status: e.target.checked })}
                    color="primary"
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Grace period (days)"
                type="number"
                variant="outlined"
                fullWidth
                value={form.grace_days_after}
                onChange={e => setForm({ ...form, grace_days_after: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Reminder days"
                type="number"
                variant="outlined"
                fullWidth
                value={form.reminder_days_before}
                onChange={e => setForm({ ...form, reminder_days_before: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.razorpay_enabled}
                    onChange={e => setForm({ ...form, razorpay_enabled: e.target.checked })}
                    color="primary"
                  />
                }
                label="Razorpay enabled"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Razorpay notes"
                variant="outlined"
                fullWidth
                value={form.razorpay_notes}
                onChange={e => setForm({ ...form, razorpay_notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={savePlan}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
});

export default SubscriptionPlans;
